import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useUserContext } from '../../contexts/UserContext';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { ExperienceContext } from '../../contexts/ExperienceContext';
import PrimaryModal from '../UI/Modal/PrimaryModal';
import Button from '../UI/Button';
import AgGridWrapper from '../UI/AgGrid';
import ConfirmModal from '../UI/Modal/confirmModal';
import { toast } from 'react-toastify';

const DeleteTemplateWithDependencyModal = ({ selectedTemplateId, setSelectedAction, setSelectedTemplate }) => {
    const { userData } = useUserContext();
    const { content } = useContentfulContext();
    const { deleteTemplate } = useContext(ExperienceContext);

    const strings = content?.DELETE_TEMPLATE_MODAL_CONTENT || {};

    const [dependencies, setDependencies] = useState(null);
    const [message, setMessage] = useState('');
    const [showGrid, setShowGrid] = useState(false);
    const [error, setErrors] = useState('');
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    // Fetch dependencies on open
    useEffect(() => {
        const fetchDependencies = async () => {
            try {
                setLoading(true);
                const result = await deleteTemplate(selectedTemplateId, true);
                if (result?.data && Object.keys(result.data).length > 0) {
                    setDependencies(result.data.data);
                    setMessage(result.data.message);
                }
            } catch (err) {
                setErrors(err?.message || strings.error_fetch);
            } finally {
                setLoading(false);
            }
        };
        fetchDependencies();
    }, [selectedTemplateId, userData]);

    const handleDeleteProduct = async () => {
        try {
            setActionLoading(true);
            const result = await deleteTemplate(selectedTemplateId);

            setSelectedAction("")
            setSelectedTemplate(null)
            toast.success(result?.message);
            setShowConfirm(false);
        } catch (error) {
            setErrors(error?.message || strings.error_delete);
        } finally {
            setActionLoading(false);
        }
    };

    // Flatten dependencies for grid preview
    const rowData = useMemo(() => {
        const rows = [];
        if (dependencies?.length > 0) {
            rows.push(
                ...dependencies?.map(d => ({
                    ...d,
                    name: d?.name,
                    type: 'Experience',
                }))
            );
        }
        return rows;
    }, [dependencies]);

    const colDefs = [
        { headerName: 'Type', field: 'type', flex: 1 },
        { headerName: 'Name', field: 'name', flex: 2 }
    ];

    return (
        <>
            <PrimaryModal loading={actionLoading} title={strings.review_dependency_title} hideModal={() => setSelectedAction("")}>
                <div className="p-6 space-y-6">
                    {loading ? (
                        <p className="text-center text-gray-500">{strings.loading_text}</p>
                    ) : (
                        <>
                            {!showGrid && (
                                <>
                                    {message ? (
                                        <p className="text-base text-black mb-4">
                                            {message} <br />
                                            <span className="font-semibold text-red-500">
                                                {strings.description_with_dependency}
                                            </span>
                                        </p>
                                    ) : (
                                        <p className="text-base text-black mb-4">
                                            {strings.description_no_dependency}
                                        </p>
                                    )}
                                    {error && <p className="text-red-500">{error}</p>}
                                    <div className="mt-6 flex justify-end gap-2.5">
                                        {message ? (
                                            <>
                                                <Button variant="outline" size="sm" onClick={() => setShowGrid(true)}>
                                                    {strings.review_dependencies_cta_text}
                                                </Button>
                                                <Button variant="danger" size="sm" onClick={() => setShowConfirm(true)}>
                                                    {strings.delete_template_cta_text}
                                                </Button>
                                            </>
                                        ) : (
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                loading={actionLoading}
                                                onClick={() => {
                                                    if (message) {
                                                        // dependencies exist → show confirm modal
                                                        setShowConfirm(true);
                                                    } else {
                                                        // no dependencies → delete directly
                                                        handleDeleteProduct();
                                                    }
                                                }}
                                            >
                                                {strings.delete_template_cta_text}
                                            </Button>
                                        )}
                                    </div>
                                </>
                            )}

                            {showGrid && (
                                <div>
                                    <p className="mb-2 font-semibold text-xs text-stride-muted uppercase tracking-wider">{strings.dependencies_preview}</p>
                                    <div className="ag-theme-quartz border border-border rounded-xl overflow-hidden" style={{ height: "300px", width: "100%" }}>
                                        <AgGridWrapper
                                            tableId="adobe-io-dependencies-table"
                                            rowData={rowData}
                                            columnDefs={colDefs}
                                        />
                                    </div>
                                    <div className="mt-4 flex justify-end">
                                        <Button variant="outline" size="sm" onClick={() => setShowGrid(false)}>
                                            {strings.back_cta_text}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </PrimaryModal>

            {showConfirm && (
                <ConfirmModal
                    title={strings.confirm_modal?.title}
                    description={strings.confirm_modal?.description}
                    ConfirmText={strings.confirm_modal?.confirm_cta_text}
                    error={error}
                    loading={actionLoading}
                    hideModal={setShowConfirm}
                    handleConfirmation={(confirmed) => {
                        if (confirmed) {
                            handleDeleteProduct();
                        } else {
                            setShowConfirm(false);
                        }
                    }}
                />
            )}
        </>
    );
};

export default DeleteTemplateWithDependencyModal;